import React from "react";

import { Box, Stack, Typography, useTheme } from "@mui/material";

import { getAddressIcon } from "utils/addressIcons";
import { ADDRESS_TYPE_LABELS, type AddressType } from "types/Address";

// Flexible address shape so this works for the address book `Address`
// (address_line) and billing/invoice `InvoiceAddress` (address) alike.
export interface AddressLike {
  type?: string | null;
  icon?: string | null;
  label?: string | null;
  full_name?: string | null;
  address_line?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
}

/**
 * Shared address row preview: type/custom-icon avatar + label (title) + a
 * formatted single line (subtitle). Single source so the address display is
 * identical across the checkout rows, pickers, tables, and detail pages
 * (they used to hand-roll it with drifting avatar shapes + icon resolution).
 * Icon resolution goes through the canonical `getAddressIcon`.
 */
export const AddressPreview = ({ address }: { address: AddressLike }) => {
  const theme = useTheme();
  const type = (address.type as AddressType) ?? "other";
  const Icon = getAddressIcon(type, address.icon);
  const title =
    address.label || ADDRESS_TYPE_LABELS[type] || "Address";
  const line = [
    address.address_line ?? address.address,
    address.city,
    address.state,
    address.postal_code,
    address.country,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={3}
      minWidth={0}
      // Fill the row so the avatar+label sit vertically centered in a table cell
      // (harmless in the auto-height InfoItem on the detail page).
      sx={{ height: "100%" }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: `1px solid ${theme.palette.primary[100]}`,
          color: theme.palette.text.secondary,
        }}
      >
        <Icon size={20} strokeWidth={1} />
      </Box>
      <Stack minWidth={0}>
        <Typography variant="body2" noWrap>
          {title}
        </Typography>
        <Typography variant="body3" color="text.secondary" noWrap>
          {line || "—"}
        </Typography>
      </Stack>
    </Stack>
  );
};

export default AddressPreview;
